import React, { useState, useEffect } from "react";
import { Plus, Trash2, FileText } from "lucide-react";

const API_BASE = "https://agssoft.ar";

interface ItemFacturable {
  descripcion: string;
  cantidad: number;
  precioUnitario: number;
  iva: number; // porcentaje, ej: 21, 10.5, 0
}

interface FacturaDatos {
  cuitEmisor: string;
  esMonotributista: boolean;
  cuitReceptor: string;
  items: ItemFacturable[];
  monedaID: number;
  concepto: 1 | 2 | 3; // 1=Productos, 2=Servicios, 3=Mixto
  pntVenta: number;
  fechaServicio?: string;
  fechaServicioHasta?: string; // <-- nuevo campo
  fechaVencimientoPago?: string;
}

interface FormData extends Omit<FacturaDatos, "cuitEmisor" | "cuitReceptor"> {
  cuitEmisor: string; // Will include formatting dashes
  cuitReceptor: string; // Will include formatting dashes
}

interface ConceptoOption {
  value: 1 | 2 | 3;
  label: string;
}

interface MonedaOption {
  value: number;
  label: string;
}

const FacturaGenerator: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    cuitEmisor: "",
    esMonotributista: false,
    cuitReceptor: "",
    items: [{ descripcion: "", cantidad: 1, precioUnitario: 0, iva: 21 }],
    monedaID: 1,
    concepto: 1,
    pntVenta: 1,
    fechaServicio: "",
    fechaServicioHasta: "", // <-- nuevo campo
    fechaVencimientoPago: "",
  });

  // NUEVO: campos de emisor adicionales y preset
  const [emisorNombre, setEmisorNombre] = useState("");
  const [emisorDomicilio, setEmisorDomicilio] = useState("");
  const [emisorIVA, setEmisorIVA] = useState("");

  // Simula el tipo de factura que devuelve la API: "A", "B" o "C"
  const [tipoFactura, setTipoFactura] = useState<"A" | "B" | "C">("A");
  const [personaReceptor, setPersonaReceptor] = useState<any>(null);
  const [loadingPersona, setLoadingPersona] = useState(false);
  const [errorPersona, setErrorPersona] = useState<string | null>(null);
  const [opcionesFactura, setOpcionesFactura] = useState<
    Array<"A" | "B" | "C">
  >(["A", "B", "C"]);
  const [sugerenciaFactura, setSugerenciaFactura] = useState<string>("");

  const conceptos: ConceptoOption[] = [
    { value: 1, label: "Productos" },
    { value: 2, label: "Servicios" },
    { value: 3, label: "Mixto" },
  ];

  const monedas: MonedaOption[] = [
    { value: 1, label: "Peso Argentino (ARS)" },
    { value: 2, label: "Dólar Estadounidense (USD)" },
    { value: 3, label: "Euro (EUR)" },
  ];

  // Opciones de IVA por producto
  const ivaOptions = [
    { value: 21, label: "21%" },
    { value: 10.5, label: "10.5%" },
    { value: 0, label: "0%" },
  ];

  const condicionVentaOptions = [
    { value: "Efectivo", label: "Efectivo" },
    { value: "Tarjeta de Crédito", label: "Tarjeta de Crédito" },
    { value: "Tarjeta de Débito", label: "Tarjeta de Débito" },
    { value: "Transferencia", label: "Transferencia" },
    { value: "Cheque", label: "Cheque" },
    { value: "Cuenta Corriente", label: "Cuenta Corriente" },
    { value: "Otra", label: "Otra" },
  ];

  const [condicionVenta, setCondicionVenta] = useState<string>("Efectivo");

  // Cargar preset de emisor al iniciar
  useEffect(() => {
    const preset = localStorage.getItem("emisorPreset");
    if (preset) {
      try {
        const data = JSON.parse(preset);
        if (data.razonSocial) setEmisorNombre(data.razonSocial);
        if (data.domicilio) setEmisorDomicilio(data.domicilio);
        if (data.condicionIVA) setEmisorIVA(data.condicionIVA);
        if (data.cuit) setFormData((prev) => ({ ...prev, cuitEmisor: data.cuit }));
      } catch {}
    }
  }, []);

  // Guardar preset
  const guardarPresetEmisor = () => {
    localStorage.setItem(
      "emisorPreset",
      JSON.stringify({
        razonSocial: emisorNombre,
        domicilio: emisorDomicilio,
        condicionIVA: emisorIVA,
        cuit: formData.cuitEmisor,
      })
    );
    alert("Preset de emisor guardado.");
  };

  const handleInputChange = <K extends keyof FormData>(
    field: K,
    value: FormData[K]
  ): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleItemChange = <K extends keyof ItemFacturable>(
    index: number,
    field: K,
    value: ItemFacturable[K]
  ): void => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData((prev) => ({
      ...prev,
      items: newItems,
    }));
  };

  const addItem = (): void => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { descripcion: "", cantidad: 1, precioUnitario: 0, iva: 21 },
      ],
    }));
  };

  const removeItem = (index: number): void => {
    if (formData.items.length > 1) {
      const newItems = formData.items.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        items: newItems,
      }));
    }
  };

  const formatCuit = (cuit: string): string => {
    const cleaned = cuit.replace(/\D/g, "");
    if (cleaned.length <= 11) {
      return cleaned.replace(/(\d{2})(\d{8})(\d{1})/, "$1-$2-$3");
    }
    return cleaned;
  };

  const handleCuitChange = (
    field: "cuitEmisor" | "cuitReceptor",
    value: string
  ): void => {
    const formatted = formatCuit(value);
    handleInputChange(field, formatted);
  };

  const calculateTotalSinIVA = (): number => {
    return formData.items.reduce((total, item) => {
      return total + item.cantidad * item.precioUnitario;
    }, 0);
  };

  // Suma el IVA de cada producto según su porcentaje
  const calculateIVA = (): number => {
    if (tipoFactura === "A") {
      return formData.items.reduce((total, item) => {
        return total + item.cantidad * item.precioUnitario * (item.iva / 100);
      }, 0);
    }
    return 0;
  };

  const calculateTotalConIVA = (): number => {
    if (tipoFactura === "A") {
      return calculateTotalSinIVA() + calculateIVA();
    }
    return calculateTotalSinIVA();
  };

  // Llama al endpoint para facturar
  const facturarAPI = async (
    facturaData: FacturaDatos,
    tipoFactura: string
  ) => {
    const token = localStorage.getItem("token") || "";
    const res = await fetch(`${API_BASE}/api/facturar`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ facturaDatos: facturaData, tipoFactura }),
    });
    if (!res.ok) throw new Error("Error al facturar");
    return res.json();
  };

  // Llama al endpoint para emitir nota 
  const emitirNotaAPI = async (
    facturaData: FacturaDatos,
    tipoFactura: string,
    tipoNota: string
  ) => {
    const token = localStorage.getItem("token") || "";
    const res = await fetch(`${API_BASE}/api/emitir-nota`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        facturaDatos: facturaData,
        tipoFactura,
        tipoNota,
      }),
    });
    if (!res.ok) throw new Error("Error al emitir nota");
    return res.json();
  };

  // Llama al endpoint para buscar persona
  const buscarPersonaAPI = async (cuit: string) => {
    const token = localStorage.getItem("token") ?? "";
    const emisorCuit = formData.cuitEmisor.replace(/\D/g, "");
    const res = await fetch(
      `${API_BASE}/api/persona/${cuit.replace(/\D/g, "")}?emisorCuit=${emisorCuit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!res.ok) throw new Error("Error al buscar persona");
    return res.json();
  };

  // Convierte fecha yyyy-mm-dd a yyyymmdd (para AFIP)
  function toAfipDate(date: string | undefined): string | undefined {
    if (!date) return undefined;
    if (/^\d{8}$/.test(date)) return date;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (m) return `${m[1]}${m[2]}${m[3]}`;
    return date;
  }

  const handleSubmit = async (): Promise<void> => {
    const processedItems: ItemFacturable[] = formData.items.map((item) => ({
      descripcion: item.descripcion,
      cantidad: Number(item.cantidad),
      precioUnitario: Number(item.precioUnitario),
      iva: item.iva,
    }));

    const facturaData: FacturaDatos & { condicionVenta?: string, razonSocial?: string, domicilioEmisor?: string, condicionIVAEmisor?: string } = {
      ...formData,
      items: processedItems,
      monedaID: Number(formData.monedaID),
      concepto: Number(formData.concepto) as 1 | 2 | 3,
      pntVenta: Number(formData.pntVenta),
      cuitEmisor: formData.cuitEmisor.replace(/\D/g, ""),
      cuitReceptor: formData.cuitReceptor.replace(/\D/g, ""),
      fechaServicio: toAfipDate(formData.fechaServicio) || undefined,
      fechaServicioHasta: toAfipDate(formData.fechaServicioHasta) || undefined,
      fechaVencimientoPago: toAfipDate(formData.fechaVencimientoPago) || undefined,
      condicionVenta,
      razonSocial: emisorNombre,
      domicilioEmisor: emisorDomicilio,
      condicionIVAEmisor: emisorIVA,
    };

    try {
      const response = await facturarAPI(facturaData, tipoFactura);
      console.log("Respuesta de facturación:", response);
      alert("Factura generada. Revisa la consola para ver los datos.");
    } catch (err) {
      alert("Error al facturar");
    }
  };

  // Ejemplo de uso de emitir nota (puedes adaptar a tu UI)
  const handleEmitirNota = async () => {
    const processedItems: ItemFacturable[] = formData.items.map((item) => ({
      descripcion: item.descripcion,
      cantidad: Number(item.cantidad),
      precioUnitario: Number(item.precioUnitario),
      iva: item.iva,
    }));

    const facturaData: FacturaDatos = {
      ...formData,
      items: processedItems,
      monedaID: Number(formData.monedaID),
      concepto: Number(formData.concepto) as 1 | 2 | 3,
      pntVenta: Number(formData.pntVenta),
      cuitEmisor: formData.cuitEmisor.replace(/\D/g, ""),
      cuitReceptor: formData.cuitReceptor.replace(/\D/g, ""),
      fechaServicio: toAfipDate(formData.fechaServicio) || undefined,
      fechaServicioHasta: toAfipDate(formData.fechaServicioHasta) || undefined,
      fechaVencimientoPago: toAfipDate(formData.fechaVencimientoPago) || undefined,
    };

    try {
      const response = await emitirNotaAPI(facturaData, tipoFactura, "Debito");
      console.log("Respuesta emitir nota:", response);
      alert("Nota emitida. Revisa la consola para ver los datos.");
    } catch (err) {
      alert("Error al emitir nota");
    }
  };

  const handleConceptoChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const value = Number(e.target.value) as 1 | 2 | 3;
    handleInputChange("concepto", value);
  };

  const handleNumberInputChange = (
    field: "monedaID" | "pntVenta",
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = Number(e.target.value);
    handleInputChange(field, value);
  };

  // Consulta silenciosa al cambiar el CUIT del receptor
  useEffect(() => {
    const cuit = formData.cuitReceptor.replace(/\D/g, "");
    if (cuit.length === 11) {
      setLoadingPersona(true);
      setErrorPersona(null);
      buscarPersonaAPI(cuit)
        .then((data) => {
          setPersonaReceptor(data.persona);

          // Lógica de opciones y sugerencia según condición
          let opciones: Array<"A" | "B" | "C"> = [];
          let sugerencia = "";
          // Si emisor es monotributista, solo C
          if (formData.esMonotributista) {
            opciones = ["C"];
            sugerencia =
              "El emisor es monotributista, solo puede emitir Factura C.";
            setTipoFactura("C");
          } else {
            // Analiza la condición del receptor
            // condicion: 1=Resp. Inscripto, 2=Consumidor Final, 5=Monotributo, otros=exento/no categorizado
            switch (data.persona?.condicion) {
              case 1:
                opciones = ["A", "B"];
                sugerencia =
                  "El receptor es Responsable Inscripto. Se recomienda Factura A.";
                setTipoFactura("A");
                break;
              case 5:
                opciones = ["B", "C"];
                sugerencia =
                  "El receptor es Monotributista. Se recomienda Factura B.";
                setTipoFactura("B");
                break;
              case 2:
              default:
                opciones = ["B", "C"];
                sugerencia =
                  "El receptor es Consumidor Final o Exento. Se recomienda Factura B.";
                setTipoFactura("B");
                break;
            }
          }
          setOpcionesFactura(opciones);
          setSugerenciaFactura(sugerencia);
        })
        .catch(() => {
          setPersonaReceptor(null);
          setErrorPersona("No se pudo consultar el receptor");
          setOpcionesFactura(formData.esMonotributista ? ["C"] : ["B", "C"]);
          setSugerenciaFactura(
            formData.esMonotributista
              ? "El emisor es monotributista, solo puede emitir Factura C."
              : "No se pudo consultar el receptor. Se recomienda Factura B."
          );
          setTipoFactura(formData.esMonotributista ? "C" : "B");
        })
        .finally(() => setLoadingPersona(false));
    } else {
      setPersonaReceptor(null);
      setOpcionesFactura(formData.esMonotributista ? ["C"] : ["B", "C"]);
      setSugerenciaFactura(
        formData.esMonotributista
          ? "El emisor es monotributista, solo puede emitir Factura C."
          : ""
      );
      setTipoFactura(formData.esMonotributista ? "C" : "B");
    }
    // eslint-disable-next-line
  }, [formData.cuitReceptor, formData.esMonotributista]);

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700">
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="flex items-center gap-2">
              <FileText className="h-6 w-6 text-blue-600" />
              <h1 className="text-2xl font-semibold text-gray-100">
                Generador de Facturas AFIP
              </h1>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Datos del Emisor */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-gray-100">
                Datos del Emisor
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    CUIT Emisor
                  </label>
                  <input
                    type="text"
                    value={formData.cuitEmisor}
                    onChange={(e) =>
                      handleCuitChange("cuitEmisor", e.target.value)
                    }
                    placeholder="20-12345678-9"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Punto de Venta
                  </label>
                  <input
                    type="number"
                    value={formData.pntVenta}
                    onChange={(e) => handleNumberInputChange("pntVenta", e)}
                    min="1"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
              </div>
              {/* NUEVO: Campos adicionales de emisor */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Razón Social / Nombre
                  </label>
                  <input
                    type="text"
                    value={emisorNombre}
                    onChange={e => setEmisorNombre(e.target.value)}
                    placeholder="Empresa S.A. / Nombre"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Domicilio
                  </label>
                  <input
                    type="text"
                    value={emisorDomicilio}
                    onChange={e => setEmisorDomicilio(e.target.value)}
                    placeholder="Domicilio"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Condición frente al IVA
                  </label>
                  <input
                    type="text"
                    value={emisorIVA}
                    onChange={e => setEmisorIVA(e.target.value)}
                    placeholder="Responsable Inscripto / Monotributista"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <button
                  type="button"
                  onClick={guardarPresetEmisor}
                  className="mt-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  Guardar datos de emisor como preset
                </button>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="monotributista"
                  checked={formData.esMonotributista}
                  onChange={(e) =>
                    handleInputChange("esMonotributista", e.target.checked)
                  }
                  className="h-4 w-4 text-blue-500 focus:ring-blue-500 border-gray-600 bg-gray-700 rounded"
                />
                <label
                  htmlFor="monotributista"
                  className="ml-2 text-sm text-gray-300"
                >
                  Es Monotributista
                </label>
              </div>
            </div>

            {/* Datos del Receptor */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-gray-100">
                Datos del Receptor
              </h2>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  CUIT Receptor
                </label>
                <input
                  type="text"
                  value={formData.cuitReceptor}
                  onChange={(e) =>
                    handleCuitChange("cuitReceptor", e.target.value)
                  }
                  placeholder="20-87654321-9"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                {loadingPersona && (
                  <div className="text-xs text-blue-400 mt-1">
                    Consultando receptor...
                  </div>
                )}
                {errorPersona && (
                  <div className="text-xs text-red-400 mt-1">
                    {errorPersona}
                  </div>
                )}
                {personaReceptor && (
                  <div className="text-xs text-green-400 mt-1">
                    {(personaReceptor.nombre ||
                      personaReceptor.razonSocial) && (
                      <>
                        {personaReceptor.nombre || personaReceptor.razonSocial}
                        {" - "}
                      </>
                    )}
                    {(() => {
                      switch (personaReceptor.condicion) {
                        case 1:
                          return "Responsable Inscripto";
                        case 5:
                          return "Monotributista";
                        case 2:
                          return "Consumidor Final";
                        default:
                          return "Exento/No categorizado";
                      }
                    })()}
                  </div>
                )}
              </div>
            </div>

            {/* Selección de tipo de factura */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Tipo de Factura
              </label>
              {opcionesFactura.length === 1 ? (
                <input
                  type="text"
                  value={`Factura ${opcionesFactura[0]}`}
                  disabled
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-400"
                />
              ) : (
                <select
                  value={tipoFactura}
                  onChange={(e) =>
                    setTipoFactura(e.target.value as "A" | "B" | "C")
                  }
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {opcionesFactura.map((op) => (
                    <option key={op} value={op}>{`Factura ${op}`}</option>
                  ))}
                </select>
              )}
              <div className="text-xs text-gray-400 mt-1">
                {sugerenciaFactura}
              </div>
            </div>

            {/* Configuración de Factura */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-gray-100">
                Configuración
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Concepto
                  </label>
                  <select
                    value={formData.concepto}
                    onChange={handleConceptoChange}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {conceptos.map((concepto) => (
                      <option key={concepto.value} value={concepto.value}>
                        {concepto.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Condición de Venta
                  </label>
                  <select
                    value={condicionVenta}
                    onChange={(e) => setCondicionVenta(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {condicionVentaOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Fechas Opcionales */}
            <div className="space-y-4">
              <h2 className="text-lg font-medium text-gray-100">
                Fechas (Opcionales)
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Fecha de Servicio Desde
                  </label>
                  <input
                    type="date"
                    value={formData.fechaServicio}
                    onChange={(e) =>
                      handleInputChange("fechaServicio", e.target.value)
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Fecha de Servicio Hasta
                  </label>
                  <input
                    type="date"
                    value={formData.fechaServicioHasta}
                    onChange={(e) =>
                      handleInputChange("fechaServicioHasta", e.target.value)
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Fecha de Vencimiento
                  </label>
                  <input
                    type="date"
                    value={formData.fechaVencimientoPago}
                    onChange={(e) =>
                      handleInputChange("fechaVencimientoPago", e.target.value)
                    }
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-100">Items</h2>
                <button
                  type="button"
                  onClick={addItem}
                  className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  Agregar Item
                </button>
              </div>

              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-3 items-end">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Descripción
                      </label>
                      <input
                        type="text"
                        value={item.descripcion}
                        onChange={(e) =>
                          handleItemChange(index, "descripcion", e.target.value)
                        }
                        placeholder="Descripción del producto/servicio"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div className="w-24">
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Cantidad
                      </label>
                      <input
                        type="number"
                        value={item.cantidad}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "cantidad",
                            Number(e.target.value)
                          )
                        }
                        min="1"
                        step="1"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    <div className="w-28">
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Precio
                      </label>
                      <input
                        type="number"
                        value={item.precioUnitario}
                        onChange={(e) =>
                          handleItemChange(
                            index,
                            "precioUnitario",
                            Number(e.target.value)
                          )
                        }
                        min="0"
                        step="0.1"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        required
                      />
                    </div>
                    {/* Solo mostrar IVA si es Factura A */}
                    {tipoFactura === "A" && (
                      <div className="w-24">
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          IVA
                        </label>
                        <select
                          value={item.iva}
                          onChange={(e) =>
                            handleItemChange(index, "iva", Number(e.target.value))
                          }
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {ivaOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="w-28 text-right">
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Subtotal
                      </label>
                      <div className="px-3 py-2 bg-gray-600 border border-gray-600 rounded-md text-sm text-gray-100">
                        ${(item.cantidad * item.precioUnitario).toFixed(2)}
                      </div>
                    </div>
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="p-2 text-red-400 hover:bg-red-950 rounded-md transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex justify-end pt-4 border-t border-gray-700">
                <div className="flex flex-col md:flex-row gap-8 text-right">
                  {tipoFactura === "A" && (
                    <>
                      <div>
                        <div className="text-sm text-gray-400">
                          Total sin IVA
                        </div>
                        <div className="text-xl font-semibold text-gray-100">
                          ${calculateTotalSinIVA().toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400">IVA</div>
                        <div className="text-xl font-semibold text-gray-100">
                          ${calculateIVA().toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-400">
                          Total con IVA
                        </div>
                        <div className="text-2xl font-bold text-gray-100">
                          ${calculateTotalConIVA().toFixed(2)}
                        </div>
                      </div>
                    </>
                  )}
                  {(tipoFactura === "B" || tipoFactura === "C") && (
                    <div>
                      <div className="text-sm text-gray-400">Total</div>
                      <div className="text-2xl font-bold text-gray-100">
                        ${calculateTotalConIVA().toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Botón de Envío */}
            <div className="flex flex-col md:flex-row gap-4 justify-end pt-6 border-t border-gray-700">
              <button
                type="button"
                onClick={handleEmitirNota}
                className="px-8 py-3 bg-purple-600 text-white font-medium rounded-md hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
              >
                Emitir Nota
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="px-8 py-3 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Generar Factura
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FacturaGenerator;
